import { useState, useRef, useEffect } from 'react';
import { MessageTypes } from '../../../interfaces/taraInterfaces';
import '../../../styles/pages/tara/chat/taraChatItemUser.scss';
import { t } from 'i18next';
import {
  copySVG,
  filledCheckboxSVG,
  saveSVG,
  warningSVG,
} from '../../../lib/allCustomSVGs';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import save_custom_prompt_from_chat_api_call from '../../../lib/api/custom-prompts/save-custom-prompt-from-chat-api-call';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../../store/reducers/appSlice';
import CircularProgress from '@mui/material/CircularProgress';

type Props = {
  message: any;
  messageType: MessageTypes;
  conversationId?: string | null;
  responseId?: string | null;
  showSaveButton?: boolean;
};

const TaraChatItemUser = ({
  message,
  messageType,
  conversationId,
  responseId,
  showSaveButton = false,
}: Props) => {
  const dispatch = useDispatch();
  const [isCopyClicked, setIsCopyClicked] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);

  const handleCopyToClipboard = async (textToCopy: string) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setIsCopyClicked(true);

      setTimeout(() => {
        setIsCopyClicked(false);
      }, 800);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const getCopyableText = () => {
    if (typeof message === 'string') {
      return message;
    }
    if (message && typeof message === 'object') {
      return JSON.stringify(message, null, 2);
    }
    return '';
  };

  const updateTooltipPosition = () => {
    if (buttonRef.current && tooltipRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const left = buttonRect.left + buttonRect.width / 2;
      const top = buttonRect.top - tooltipRect.height - 0.5;
      setTooltipPosition({ top, left });
    }
  };

  useEffect(() => {
    if (isHovered) {
      requestAnimationFrame(() => {
        updateTooltipPosition();
      });
      const handleScroll = () => updateTooltipPosition();
      const handleResize = () => updateTooltipPosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isHovered]);

  const handleSavePrompt = async () => {
    if (!conversationId || !responseId || isSaving || isSaved) return;

    const promptText = getCopyableText();
    if (!promptText.trim()) return;

    setIsSaving(true);
    setHasError(false);
    try {
      const result = await save_custom_prompt_from_chat_api_call(
        conversationId,
        responseId,
        promptText
      );
      if (result?.success) {
        dispatch(
          setMessage({
            message: t('CustomPromptSavedSuccessfully'),
            messageType: 'success',
          })
        );
        setIsSaved(true);
        setTimeout(() => {
          setIsSaved(false);
        }, 2000);
      } else {
        const errorMessage = result?.message || 'Failed to save prompt';
        dispatch(
          setMessage({
            message: errorMessage,
            messageType: 'warning',
          })
        );
        setHasError(true);
        setTimeout(() => {
          setHasError(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
      dispatch(
        setMessage({
          message: 'An error occurred while saving the prompt',
          messageType: 'error',
        })
      );
      setHasError(true);
      setTimeout(() => {
        setHasError(false);
      }, 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const renderMessage = () => {
    if (messageType === 'chart') {
      return <div className='taraChatItemUser__chart'>{message}</div>;
    } else if (messageType === 'table') {
      return <div className='taraChatItemUser__table'>{message}</div>;
    } else {
      return <div className='taraChatItemUser__message'>{message}</div>;
    }
  };

  return (
    <div className='taraChatItemUser'>
      <div className='taraChatItemUser__copy-container'>
        {showSaveButton && conversationId && responseId && (
          <div className='taraChatItemUser__save-button-wrapper'>
            {isHovered && (
              <span
                ref={tooltipRef}
                className='taraChatItemUser__save-button-tooltip'
                style={{
                  top: `${tooltipPosition.top}px`,
                  left: `${tooltipPosition.left}px`,
                }}
              >
                {isSaved
                  ? t('CustomPromptSaved')
                  : isSaving
                  ? t('SavingCustomPrompt')
                  : hasError
                  ? t('ErrorSavingCustomPrompt')
                  : t('SaveAsCustomPrompt')}
              </span>
            )}
            <button
              ref={buttonRef}
              className='taraChatItemUser__save-button'
              data-saved={isSaved.toString()}
              data-error={hasError.toString()}
              data-saving={isSaving.toString()}
              onClick={handleSavePrompt}
              disabled={isSaving || isSaved}
              onMouseEnter={() => {
                setIsHovered(true);
              }}
              onMouseLeave={() => setIsHovered(false)}
            >
              {isSaved ? (
                <CustomSVGs svg={filledCheckboxSVG} />
              ) : isSaving ? (
                <CircularProgress
                  size={18}
                  style={{ color: 'var(--main-text-color)' }}
                />
              ) : hasError ? (
                <CustomSVGs svg={warningSVG} />
              ) : (
                <CustomSVGs svg={saveSVG} />
              )}
            </button>
          </div>
        )}
        <button
          className='taraChatItemUser__copy-button'
          data-copied={isCopyClicked.toString()}
          onClick={() => handleCopyToClipboard(getCopyableText())}
          title={t('CopyToClipboard')}
        >
          {!isCopyClicked ? (
            <CustomSVGs svg={copySVG} />
          ) : (
            <CustomSVGs svg={filledCheckboxSVG} />
          )}
        </button>
      </div>
      {renderMessage()}
      {/* <div className='taraChatItemUser__icon'> 👤</div> */}
    </div>
  );
};

export default TaraChatItemUser;
